/**
 * `.vn2r-session` files: portable, file-based personal config that
 * captures *where the user was* in the workspace: current route,
 * search params, layout mode, loaded IFCs (as filename/sha256 hints,
 * since the browser can't reopen disk files automatically).
 *
 * Cloud remains the source of truth for collaborative data (BCF
 * topics, comments, viewpoints, members, modules); the file just
 * restores the personal slice of UI state on another machine. Open
 * navigates to the saved route and shows a banner listing the IFCs
 * that the user should reload from disk.
 */
import { unzip, zip } from 'fflate'
import type { WorkspaceLayoutMode } from '@core/store/workspaceLayoutSlice'
import type { LoadedBcf } from '@modules/bcf/store/bcfClientSlice'
import type { LocalViewpoint } from '@viewer/lib/viewpointsStore'
import type { GeoPlacement } from '@viewer/lib/geoTransform'
import type { VnSessionConflitos } from '@viewer/clash/clashSessao'

export const SESSION_KIND = 'vn2r-openbim-session'
export const SESSION_VERSION = 7
/** Versions the parser still accepts on read. v2 has no `bcfs` /
 * `viewpoints` fields and is treated as "blank" for those slices.
 * v3 has no `placement_override` per model (Onda C). v4 adds it.
 * v5 acrescenta o trabalho que vivia só em localStorage (vistas 2D e
 * rascunhos IDS/IDSX): sem ele, "Guardar sessão" não guardava tudo e a
 * regra "o trabalho só sobrevive se gravares" era uma promessa falsa.
 * v6 acrescenta `source_format` e permite guardar `.frag` nativo no
 * contentor, sem o tratar como IFC editável.
 * v7 acrescenta `conflitos`: conjuntos, configuração, triagem (com os
 * grupos da proposta), histórico das corridas e o identificador dos
 * Guids BCF. As anteriores leem-se sem a secção (a triagem vinha no
 * `local_work`).
 * Ainda na v7, o campo opcional `partilhado` por modelo (modelos
 * partilhados no workspace): numa sessão leve os bytes desses modelos
 * NÃO vêm no contentor e quem abre vai buscá-los ao workspace pelo
 * SHA-256. Não muda a versão de propósito: uma versão anterior da
 * aplicação lê o ficheiro na mesma e pede esses IFC do disco, que é o
 * comportamento certo para quem não tem o módulo. */
export const ACCEPTED_SESSION_VERSIONS = [2, 3, 4, 5, 6, 7] as const

/** Per-model geo placement override embedded in the session, mirroring
 *  the in-memory GeoPlacement type. Stored in IFC convention (Eastings,
 *  Northings, OrthogonalHeight in metres; rotation in degrees CCW from
 *  east; isotropic scale). Absent / null = no override (use the
 *  IfcMapConversion baseline at load time). */
export type VnSessionPlacement = GeoPlacement

export interface VnSessionModel {
  filename: string
  sha256: string
  schema_version: string | null
  /** v4+: manual placement saved by Onda B's sidebar editor. */
  placement_override?: VnSessionPlacement | null
  /** v6+: original source. Missing means IFC for backwards compatibility. */
  source_format?: 'ifc' | 'frag'
  /** Opcional (v7): está nos modelos partilhados do workspace (pelo SHA-256). Numa
   *  sessão leve os bytes não vêm no ficheiro e são descarregados do
   *  workspace ao abrir; ver core/modelos. */
  partilhado?: boolean
}

/** Snapshot of a `LocalViewpoint` with the thumbnail Blob encoded as a
 * data URL so the whole viewpoint fits in JSON. */
export interface VnSerialViewpoint {
  record: Omit<LocalViewpoint, 'thumbnail'>
  thumbnail_data_url: string | null
  thumbnail_mime: string | null
}

export interface VnSession {
  kind: typeof SESSION_KIND
  version: number
  saved_at: string
  /** Slug do workspace onde a sessão foi gravada. VAZIO quando foi
   *  gravada em modo local: aí não há workspace nenhum, e um ficheiro
   *  sem dono abre em qualquer lado (ver `sessionBelongsTo`). */
  workspace_slug: string
  /** Path after `/${workspace_slug}/`. Empty string for the viewer, e
   *  sempre vazio em modo local (onde o URL não escolhe nada: o router
   *  serve o viewer em qualquer caminho). */
  route: string
  /** `URLSearchParams.toString()` (no leading `?`). */
  search: string
  layout_mode: WorkspaceLayoutMode
  models: VnSessionModel[]
  /** Full payload of every BCF the user had open. Absent on v2. */
  bcfs?: LoadedBcf[]
  /** Which BCF was active in the side panel. Absent on v2. */
  bcf_active_id?: string | null
  /** Every local viewpoint at save time, with thumbnails as data URLs.
   * Absent on v2. */
  viewpoints?: VnSerialViewpoint[]
  /** v5+: fotografia crua das chaves de TRABALHO em localStorage
   * (`vn2r.views2d.<sha>`, `vn2r.ids.draft`, `vn2r.idsx.draft`). Fica
   * como string para o formato de cada uma poder evoluir sozinho: quem
   * lê é o store respectivo, que já sabe validar e migrar. Preferências
   * de UI NUNCA entram aqui. */
  local_work?: VnSessionLocalWork[]
  /** v7+: a detecção de conflitos (ver viewer/clash/clashSessao.ts).
   *  Ausente nas sessões anteriores e quando não há nada de conflitos. */
  conflitos?: VnSessionConflitos | null
}

/** Uma chave de trabalho em localStorage, tal e qual. */
export interface VnSessionLocalWork {
  key: string
  raw: string
}

export interface BuildSessionOpts {
  /** Vazio (ou omitido) em modo local. */
  workspaceSlug?: string
  /** Vazio em modo local. */
  route?: string
  search?: string
  layoutMode: WorkspaceLayoutMode
  models: VnSessionModel[]
  bcfs?: LoadedBcf[]
  bcfActiveId?: string | null
  viewpoints?: VnSerialViewpoint[]
  localWork?: VnSessionLocalWork[]
  conflitos?: VnSessionConflitos | null
}

export function buildSession(opts: BuildSessionOpts): VnSession {
  return {
    kind: SESSION_KIND,
    version: SESSION_VERSION,
    saved_at: new Date().toISOString(),
    workspace_slug: opts.workspaceSlug ?? '',
    route: opts.route ?? '',
    search: opts.search ?? '',
    layout_mode: opts.layoutMode,
    models: opts.models,
    bcfs: opts.bcfs ?? [],
    bcf_active_id: opts.bcfActiveId ?? null,
    viewpoints: opts.viewpoints ?? [],
    local_work: opts.localWork ?? [],
    ...(opts.conflitos ? { conflitos: opts.conflitos } : {}),
  }
}

/**
 * Nome de ficheiro sugerido: `<slug>_<data>.vn2r-session`. Sem
 * workspace (modo local) o prefixo é `vn2r`, que é o que faz sentido
 * num ficheiro que não pertence a espaço nenhum.
 */
export function sessionFileName(
  workspaceSlug: string | null | undefined,
  savedAtIso: string = new Date().toISOString(),
): string {
  const ws = (workspaceSlug ?? '')
    .normalize('NFKD')
    .replace(/[^\w\-.]/g, '')
    .slice(0, 40)
  return `${ws || 'vn2r'}_${savedAtIso.slice(0, 10)}.vn2r-session`
}

export function suggestedFilename(session: VnSession): string {
  return sessionFileName(session.workspace_slug, session.saved_at)
}

/**
 * Um ficheiro pode ser aberto neste workspace?
 *
 * Regra, nos dois sentidos:
 *  - sessão gravada em LOCAL (`workspace_slug` vazio) abre em qualquer
 *    lado, incluindo dentro de um workspace;
 *  - em modo local (`current` a null) abre-se qualquer ficheiro, venha
 *    de que workspace vier: o que for cloud-only ignora-se ao repor;
 *  - dentro de um workspace, um ficheiro de OUTRO workspace continua a
 *    ser recusado (os dados colaborativos não são intermutáveis).
 */
export function sessionBelongsTo(
  session: VnSession,
  current: string | null | undefined,
): boolean {
  if (!current) return true
  if (!session.workspace_slug) return true
  return session.workspace_slug === current
}

export function downloadSession(session: VnSession): void {
  const blob = new Blob([JSON.stringify(session, null, 2)], {
    type: 'application/json',
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = suggestedFilename(session)
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export interface SaveSessionResult {
  /** True when the user confirmed a save (picker or fallback download). */
  saved: boolean
  /** Final filename used (the picker can rename). */
  filename: string | null
}

/** IFC bytes to embed in a session zip, keyed by SHA-256. The session
 * file becomes a self-contained snapshot; opening it on another
 * machine reloads the exact same IFCs without needing the original
 * file path (browsers don't expose paths to JS in the first place). */
export interface VnSessionIfcBytes {
  sha256: string
  filename: string
  bytes: Uint8Array
  sourceFormat?: 'ifc' | 'frag'
}

/** Zip a manifest + IFC bytes into the `.vn2r-session` container.
 * Uses fflate's default deflate level (6); IFC text typically halves
 * in size at that setting and going higher buys very little. */
async function packSessionZip(
  session: VnSession,
  ifcs: VnSessionIfcBytes[],
): Promise<Uint8Array> {
  const manifestBytes = new TextEncoder().encode(
    JSON.stringify(session, null, 2),
  )
  const entries: Record<string, Uint8Array> = {
    'manifest.json': manifestBytes,
  }
  for (const ifc of ifcs) {
    // Filename inside the zip uses the SHA so we can map back from
    // `manifest.models[].sha256` on read.
    const path =
      ifc.sourceFormat === 'frag'
        ? `fragments/${ifc.sha256}.frag`
        : `ifcs/${ifc.sha256}.ifc`
    entries[path] = ifc.bytes
  }
  return await new Promise<Uint8Array>((resolve, reject) => {
    zip(entries, (err, data) => {
      if (err) reject(err)
      else resolve(data)
    })
  })
}

/** Unzip a `.vn2r-session` container into manifest + IFC bytes map. */
async function unpackSessionZip(
  buf: Uint8Array,
): Promise<{ session: VnSession; ifcBytes: Map<string, Uint8Array> }> {
  const entries = await new Promise<Record<string, Uint8Array>>(
    (resolve, reject) => {
      unzip(buf, (err, data) => {
        if (err) reject(err)
        else resolve(data)
      })
    },
  )
  const manifestRaw = entries['manifest.json']
  if (!manifestRaw) {
    throw new SessionParseError('Container sem manifest.json.')
  }
  const text = new TextDecoder().decode(manifestRaw)
  const session = parseSession(text)
  const ifcBytes = new Map<string, Uint8Array>()
  for (const [name, bytes] of Object.entries(entries)) {
    const m = /^(?:ifcs\/([0-9a-f]+)\.ifc|fragments\/([0-9a-f]+)\.frag)$/i.exec(
      name,
    )
    const sha = m?.[1] ?? m?.[2]
    if (sha) ifcBytes.set(sha.toLowerCase(), bytes)
  }
  return { session, ifcBytes }
}

/** First four bytes of any zip file are 'PK\x03\x04'. */
function isZipBuffer(buf: Uint8Array): boolean {
  return (
    buf.length >= 4 &&
    buf[0] === 0x50 &&
    buf[1] === 0x4b &&
    buf[2] === 0x03 &&
    buf[3] === 0x04
  )
}

/**
 * Read a `.vn2r-session` container. Detects zip-wrapped sessions
 * (current format, snapshot-style with embedded IFC bytes) vs plain
 * JSON sessions (v2 / v3, no embedded IFCs) and returns both shapes
 * on the same envelope. `ifcBytes` is empty for JSON sessions.
 */
export async function readSessionContainer(
  file: File,
): Promise<{ session: VnSession; ifcBytes: Map<string, Uint8Array> }> {
  const buf = new Uint8Array(await file.arrayBuffer())
  if (isZipBuffer(buf)) {
    return await unpackSessionZip(buf)
  }
  const text = new TextDecoder().decode(buf)
  const session = parseSession(text)
  return { session, ifcBytes: new Map() }
}

type SaveFilePickerOpts = {
  suggestedName?: string
  types?: { description: string; accept: Record<string, string[]> }[]
}
type WindowWithSave = Window & {
  showSaveFilePicker?: (opts: SaveFilePickerOpts) => Promise<FileSystemFileHandle>
}

/** True when the FS Access API is available for native Save As. */
export function supportsSavePicker(): boolean {
  return typeof (window as WindowWithSave).showSaveFilePicker === 'function'
}

export type PickHandleResult =
  | { kind: 'handle'; handle: FileSystemFileHandle }
  | { kind: 'cancelled' }
  | { kind: 'unsupported' }

/** Open the native Save As picker. Must be called synchronously from
 * a user gesture (no awaits between the click handler and this call):
 * Chromium consumes the transient activation here and returns a
 * handle that the caller can write to even after the activation
 * expires.
 *
 * Returns a tagged result so the caller can distinguish:
 *  - `handle`: user picked a destination, we have a writable handle.
 *  - `cancelled`: user dismissed the picker; do NOT fall back to a
 *    silent download (that would feel like the cancel was ignored).
 *  - `unsupported`: API not available (Firefox, older Safari); the
 *    caller falls back to anchor download.
 *
 * No `types` filter is passed. Chrome silently rejects custom MIME +
 * extension combinations on some setups, leaving the picker not
 * showing at all. The caller drives the suggested name with the
 * `.vn2r-session` extension, so we don't need an accept filter. */
export async function pickSessionSaveHandle(
  suggestedName: string,
): Promise<PickHandleResult> {
  const w = window as WindowWithSave
  if (typeof w.showSaveFilePicker !== 'function') {
    console.info('[session] showSaveFilePicker not available; anchor fallback')
    return { kind: 'unsupported' }
  }
  try {
    const handle = await w.showSaveFilePicker({ suggestedName })
    return { kind: 'handle', handle }
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      return { kind: 'cancelled' }
    }
    console.warn('[session] showSaveFilePicker threw', err)
    return { kind: 'unsupported' }
  }
}

/**
 * Write the session payload to disk. When `handle` is set, writes via
 * the FS Access API (caller must have acquired the handle inside the
 * click gesture via `pickSessionSaveHandle`). When `handle` is null,
 * falls back to anchor download under the suggested name. When `ifcs`
 * is non-empty we emit a zip container (manifest.json + ifcs/*.ifc);
 * otherwise a plain JSON file with the same `.vn2r-session` extension.
 */
export async function writeSessionFile(
  handle: FileSystemFileHandle | null,
  suggestedName: string,
  session: VnSession,
  ifcs: VnSessionIfcBytes[] = [],
): Promise<SaveSessionResult> {
  const payload =
    ifcs.length > 0
      ? await packSessionZip(session, ifcs)
      : new TextEncoder().encode(JSON.stringify(session, null, 2))
  const mime = ifcs.length > 0 ? 'application/zip' : 'application/json'

  if (handle) {
    try {
      const writable = await handle.createWritable()
      // .slice().buffer is the workaround used elsewhere in this
      // repo for `Uint8Array<ArrayBufferLike>` not assignable to
      // `BufferSource`/`BlobPart` under the new lib.dom typings.
      await writable.write(payload.slice().buffer)
      await writable.close()
      return { saved: true, filename: handle.name ?? suggestedName }
    } catch (err) {
      console.warn('[session] writable failed, falling back to download', err)
      // fall through to anchor download
    }
  }

  const blob = new Blob([payload.slice().buffer], { type: mime })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = suggestedName
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 1000)
  return { saved: true, filename: suggestedName }
}

/** Encode a Blob as a data URL with its mime type, suitable for
 * embedding in JSON and decoding back to a Blob on the other side. */
export async function blobToDataUrl(blob: Blob): Promise<string> {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'))
    reader.readAsDataURL(blob)
  })
}

/** Inverse of `blobToDataUrl`. Returns null when the input is not a
 * recognisable data URL, including a damaged payload (invalid base64 or
 * percent-encoding). `atob` and `decodeURIComponent` throw on those, and
 * the session restore calls this inside the viewpoint loop: one bad
 * thumbnail used to abort the loop after the existing viewpoints had
 * already been cleared, losing every viewpoint after it. */
export function dataUrlToBlob(dataUrl: string): Blob | null {
  const m = /^data:([^;,]+)(?:;base64)?,(.*)$/.exec(dataUrl)
  if (!m) return null
  const mime = m[1] || 'application/octet-stream'
  const isBase64 = dataUrl.includes(';base64,')
  const payload = m[2]
  try {
    if (isBase64) {
      const bin = atob(payload)
      const bytes = new Uint8Array(bin.length)
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
      return new Blob([bytes], { type: mime })
    }
    return new Blob([decodeURIComponent(payload)], { type: mime })
  } catch {
    return null
  }
}

export class SessionParseError extends Error {}

export function parseSession(text: string): VnSession {
  let raw: unknown
  try {
    raw = JSON.parse(text)
  } catch {
    throw new SessionParseError('Ficheiro inválido (JSON malformado).')
  }
  if (!raw || typeof raw !== 'object') {
    throw new SessionParseError('Ficheiro de sessão inválido.')
  }
  const obj = raw as Record<string, unknown>
  if (obj.kind !== SESSION_KIND) {
    throw new SessionParseError('Não é um ficheiro .vn2r-session.')
  }
  if (
    typeof obj.version !== 'number' ||
    !ACCEPTED_SESSION_VERSIONS.includes(
      obj.version as (typeof ACCEPTED_SESSION_VERSIONS)[number],
    )
  ) {
    throw new SessionParseError(
      `Versão de sessão não suportada (${String(obj.version)}). Esta plataforma usa versão ${SESSION_VERSION}.`,
    )
  }
  if (typeof obj.workspace_slug !== 'string') {
    throw new SessionParseError('Workspace em falta no ficheiro.')
  }
  if (typeof obj.route !== 'string' || typeof obj.search !== 'string') {
    throw new SessionParseError('Rota inválida no ficheiro.')
  }
  if (!Array.isArray(obj.models)) {
    throw new SessionParseError('Lista de modelos inválida.')
  }
  return obj as unknown as VnSession
}

export async function readSessionFile(file: File): Promise<VnSession> {
  const txt = await file.text()
  return parseSession(txt)
}
